import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import {
  Box,
  Button,
  TextInput,
  Typography
} from "@dealer-center/core-components";
import type { ConfigurationPreviewProps } from "@dealer-center/ddc-pages-nrt-provider-config-tool-interface";
import {
  CARD_SX,
  CODE_BLOCK_SX,
  FIELD_GROUP_SX,
  PANEL_HEADING_SX,
  SEARCH_MATCH_SX
} from "./panelStyles";

const randomVehicleId = () =>
  `vehicle-${Math.random().toString(36).slice(2, 11)}`;

// Shown on every disabled control until a Provider is picked.
const GUIDANCE_PLACEHOLDER = "Select a provider first";

// Wraps every case-insensitive occurrence of `needle` in `text` with a
// highlighted <mark>; keyed on match position since matches never overlap.
const highlightMatches = (text: string, needle: string): ReactNode[] => {
  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let matchIndex = lowerText.indexOf(lowerNeedle, cursor);
  while (matchIndex !== -1) {
    if (matchIndex > cursor) nodes.push(text.slice(cursor, matchIndex));
    nodes.push(
      <Box component="mark" key={`match-${matchIndex}`} sx={SEARCH_MATCH_SX}>
        {text.slice(matchIndex, matchIndex + needle.length)}
      </Box>
    );
    cursor = matchIndex + needle.length;
    matchIndex = lowerText.indexOf(lowerNeedle, cursor);
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
};

export const ConfigurationPreview = ({
  disabled,
  jsonData,
  searchQuery,
  vehicleId,
  onSearchChange,
  onVehicleIdChange
}: ConfigurationPreviewProps) => {
  const handleGenerateVehicle = useCallback(
    () => onVehicleIdChange(randomVehicleId()),
    [onVehicleIdChange]
  );

  const handleClearSearch = useCallback(
    () => onSearchChange(""),
    [onSearchChange]
  );

  const jsonString = useMemo(
    () => JSON.stringify(jsonData, null, 2),
    [jsonData]
  );
  const filteredJson = useMemo(() => {
    if (!searchQuery) return jsonString;
    const needle = searchQuery.toLowerCase();
    return jsonString
      .split("\n")
      .filter(line => line.toLowerCase().includes(needle))
      .join("\n");
  }, [jsonString, searchQuery]);

  const previewContent = useMemo(() => {
    if (!searchQuery || !filteredJson) return filteredJson;
    return highlightMatches(filteredJson, searchQuery);
  }, [filteredJson, searchQuery]);

  return (
    <Box sx={CARD_SX}>
      <Typography sx={PANEL_HEADING_SX} variant="h4">
        Configuration Preview
      </Typography>

      {/* Grid so the button below lines up with the input, not the label. */}
      <Box
        sx={{
          ...FIELD_GROUP_SX,
          alignItems: "center",
          columnGap: 2,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          rowGap: 1
        }}
      >
        <Typography variant="body-sm">Vehicle ID</Typography>
        <TextInput
          disabled={disabled}
          displayLabel={false}
          id="vehicleId"
          label="Vehicle ID"
          name="vehicleId"
          placeholder={disabled ? GUIDANCE_PLACEHOLDER : undefined}
          value={vehicleId}
          onChange={e => onVehicleIdChange(e.target.value)}
        />
        <Button
          analyticsConfiguration={{
            eventName: "nrt_provider_config_generate_vehicle"
          }}
          buttonStyle="secondary"
          disabled={disabled}
          sx={{
            gridColumn: 2,
            justifySelf: "end",
            whiteSpace: "nowrap",
            width: "fit-content"
          }}
          onClick={handleGenerateVehicle}
        >
          Just give me a vehicle!
        </Button>
      </Box>

      <Box
        sx={{
          ...FIELD_GROUP_SX,
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 2
        }}
      >
        <Typography variant="body-sm">Search JSON Content</Typography>
        <Box sx={{ flex: "1 1 12rem", minWidth: 0 }}>
          <TextInput
            disabled={disabled}
            displayLabel={false}
            id="searchQuery"
            label="Search JSON Content"
            name="searchQuery"
            placeholder={
              disabled ? GUIDANCE_PLACEHOLDER : "Search within JSON data"
            }
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
          />
        </Box>
        <Button
          analyticsConfiguration={{
            eventName: "nrt_provider_config_clear_search"
          }}
          buttonStyle="secondary"
          disabled={disabled}
          sx={{ marginLeft: "auto" }}
          onClick={handleClearSearch}
        >
          Clear
        </Button>
      </Box>

      <Box data-testid="json-preview" sx={CODE_BLOCK_SX}>
        {disabled
          ? "Select a provider to view the vehicle payload."
          : previewContent || `No lines match "${searchQuery}"`}
      </Box>
    </Box>
  );
};
